import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../../../store';
import * as triplinkService from '../../../utils/triplinkService';
import { finishStripeCheckout } from '../../thunks/checkout';
import { logout } from '../auth/authSlice';

// Thunks

export const fetchEntitledFeatures = createAsyncThunk(
  'subscriptions/fetchEntitledFeatures',
  async (params, { dispatch, getState }) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;//FIXME need to ensure this token is not expired
    if (!accessToken) {
      throw new Error('cannot fetchEntitledFeatures without access');
    }
    const result = await triplinkService.getEntitledFeatures(accessToken);
    return {
      features: result,
      requiresAccessToken: true, 
    };
  }
)

export const fetchSubscription = createAsyncThunk(
  'subscriptions/fetchSubscription',
  async (params, { dispatch, getState }) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;//FIXME need to ensure this token is not expired
    if (!accessToken) {
      throw new Error('cannot fetchSubscription without access');
    }
    const result = await triplinkService.getEffectiveSubscription(accessToken);
    return {
      subscriptions: result,
      requiresAccessToken: true,
    };
  }
)

/**
 * Creating a portal session is in this Redux slice for convenient access to
 * the "auth access token" needed by the server API request, not because it has
 * anything to put into the state.
 */
export const createStripePortalSession = createAsyncThunk(
  'subscriptions/createStripePortalSession',
  async (params: {}, { dispatch, getState }) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;//FIXME need to ensure this token is not expired
    if (!accessToken) {
      throw new Error('cannot createStripePortalSession without access');
    }
    const result = await triplinkService.createStripePortalSession(
        accessToken, params
    );
    return result;
  }
)

/**
 * Creating a portal session is in this Redux slice for convenient access to
 * the "auth access token" needed by the server API request, not because it has
 * anything to put into the state.
 */
export const changeSubscriptionProducts = createAsyncThunk(
  'subscriptions/changeSubscriptionProducts',
  async (params: {}, { dispatch, getState }) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;//FIXME need to ensure this token is not expired
    if (!accessToken) {
      throw new Error('cannot createStripePortalSession without access');
    }
    const result = await triplinkService.changeSubscriptionProducts(
        accessToken, params
    );
    // Fetch the subscription after a delay, hopefully it will have finished
    // updating by then.
    //TODO also fetch updated business and business_member
    await new Promise(resolve => setTimeout(resolve, 10000));
    try {
      console.log('delayed fetchSubscription')
      await dispatch(fetchSubscription());
      await dispatch(fetchEntitledFeatures());
    } catch(error) {
      console.error(error)
    }
    return result;
  }
)

/**
 * Changing subscription quantity is in this Redux slice for convenient access
 * to the "auth access token" needed by the server API request, not because it
 * has anything to put into the state.
 */
export const changeSubscriptionQuantity = createAsyncThunk(
  'subscriptions/changeSubscriptionQuantity',
  async (params: {}, { dispatch, getState }) => {
    const state: RootState = (getState() as RootState);
    const accessToken = state.accessToken.body;//FIXME need to ensure this token is not expired
    if (!accessToken) {
      throw new Error('cannot changeSubscriptionQuantity without access');
    }
    const result = await triplinkService.changeSubscriptionQuantity(
        accessToken, params
    );
    // Fetch the subscription after a delay, hopefully it will have finished
    // updating by then.
    await new Promise(resolve => setTimeout(resolve, 10000));
    try {
      console.log('delayed fetchSubscription')
      await dispatch(fetchSubscription());
    } catch(error) {
      console.error(error)
    }
    return result;
  }
)

// Define subscriptions state

export interface SubscriptionsState {
  subscription: triplinkService.SubscriptionInterface | null;
  updatingSubscription: boolean;
  entitledFeatures: string[];
  updatingEntitledFeatures: boolean;
}

const initialState: SubscriptionsState = {
  subscription: null,
  updatingSubscription: false,
  entitledFeatures: [],
  updatingEntitledFeatures: false,
};

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
  },
  extraReducers(builder) {
    builder
      .addCase(fetchEntitledFeatures.pending, (state, action) => {
        state.updatingEntitledFeatures = true;
      })
      .addCase(fetchEntitledFeatures.fulfilled, (state, action) => {
        state.updatingEntitledFeatures = false;
        state.entitledFeatures = action.payload.features;
      })
      .addCase(fetchEntitledFeatures.rejected, (state, action) => {
        state.updatingEntitledFeatures = false;
      })
      .addCase(fetchSubscription.pending, (state, action) => {
        state.updatingSubscription = true;
      })
      .addCase(fetchSubscription.fulfilled, (state, action) => {
        state.updatingSubscription = false;
        state.subscription = action.payload.subscriptions;
      })
      .addCase(fetchSubscription.rejected, (state, action) => {
        state.updatingSubscription = false;
      })
      .addCase(finishStripeCheckout.fulfilled, (state, action) => {
        state.subscription = action.payload.subscription;
      })
      .addCase(logout, (state) => {
        state.subscription = null
        state.updatingSubscription = false
        state.entitledFeatures = []
        state.updatingEntitledFeatures = false
      })
  },
});

//export const { 
//} = subscriptionsSlice.actions
export const selectSubscriptions = (state: RootState) => state.subscriptions;
export default subscriptionsSlice.reducer;
